import type { GetServiceNormalized as Service } from '@messenger/types'

type ServiceType = 'service' | 'visitation'

/**
 * Ensures that each service type (service, visitation) has EXACTLY one default.
 *
 * Handles two cases:
 * 1. "Too many defaults" - If multiple items of the same type have default=true,
 *    only the FIRST one keeps default=true, others are set to false.
 * 2. "Too few defaults" - If no item of a type has default=true,
 *    the first item of that type becomes the default.
 *
 * This allows TWO items to have default=true simultaneously (one per type),
 * enabling layouts to have both a default service and default visitation.
 *
 * This function is idempotent - running it multiple times produces the same result.
 */
export const ensureDefaultService = (services: Service[]): Service[] => {
  if (!services || services.length === 0) return services

  // Create a copy to avoid mutating the original array
  const result = services.map((s) => ({ ...s }))

  // Process each type independently
  const types: ServiceType[] = ['service', 'visitation']

  types.forEach((type) => {
    const itemsOfType = result.filter((s) => s.type?.toLowerCase() === type)

    if (itemsOfType.length === 0) return

    // Find all items of this type that have default=true
    const defaultsOfType = itemsOfType.filter((s) => s.default === true)

    if (defaultsOfType.length > 1) {
      // Too many defaults: keep only the first one, unset others
      let foundFirst = false
      result.forEach((s) => {
        if (s.type?.toLowerCase() === type && s.default === true) {
          if (foundFirst) {
            s.default = false
          } else {
            foundFirst = true
          }
        }
      })
    } else if (defaultsOfType.length === 0) {
      // No defaults found: set the first item of this type as default
      const firstOfType = result.find((s) => s.type?.toLowerCase() === type)
      if (firstOfType) {
        firstOfType.default = true
      }
    }
    // If exactly 1 default exists, do nothing - it's already correct
  })

  return result
}
